import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { apiClient } from '@/services/apiClient';
import { resolveBundlePrivileges } from '@/lib/privileges';
import type {
  MePrivilegesResponse,
  PrivilegeKey,
  Role,
} from '@/types/privileges.types';

interface PrivilegeContextValue {
  roles: Role[];
  privileges: PrivilegeKey[];
  isLoading: boolean;
  /** Source: 'api' when backend supplied them, 'fallback' when derived from local role. */
  source: 'api' | 'fallback' | 'none';
  has: (key: PrivilegeKey) => boolean;
  hasAny: (keys: PrivilegeKey[]) => boolean;
  hasAll: (keys: PrivilegeKey[]) => boolean;
  hasRole: (role: Role) => boolean;
  /** Force a re-fetch (e.g. after a super admin updates someone's privileges). */
  refresh: () => Promise<void>;
}

const PrivilegeContext = createContext<PrivilegeContextValue | undefined>(undefined);

/**
 * Reads the locally stored admin user (set by AdminLogin) to derive a fallback
 * role list. Lets the frontend work end-to-end before `/me/privileges` exists
 * on the backend.
 */
const readLocalAdminRoles = (): Role[] => {
  try {
    const raw = localStorage.getItem('adminUser');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const candidates: unknown[] = [];
    if (parsed?.role) candidates.push(parsed.role);
    if (Array.isArray(parsed?.roles)) candidates.push(...parsed.roles);
    return candidates.filter((r): r is Role => typeof r === 'string') as Role[];
  } catch {
    return [];
  }
};

export const PrivilegeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [privileges, setPrivileges] = useState<PrivilegeKey[]>([]);
  const [source, setSource] = useState<'api' | 'fallback' | 'none'>('none');
  const [isLoading, setIsLoading] = useState(true);

  const load = async () => {
    setIsLoading(true);
    const isAdminAuthed = localStorage.getItem('isAdminAuthenticated') === 'true';

    if (!isAdminAuthed) {
      setRoles([]);
      setPrivileges([]);
      setSource('none');
      setIsLoading(false);
      return;
    }

    // Try the API first — when backend ships, this becomes the source of truth.
    try {
      const response = await apiClient.get<MePrivilegesResponse>('/me/privileges');
      if (response?.success && response.data) {
        const apiRoles = Array.isArray(response.data.roles) ? response.data.roles : [];
        const apiPrivs = Array.isArray(response.data.privileges) ? response.data.privileges : [];
        setRoles(apiRoles);
        setPrivileges(apiPrivs);
        setSource('api');
        setIsLoading(false);
        return;
      }
    } catch {
      /* fall through to local fallback */
    }

    // Fallback: derive roles from localStorage adminUser + role bundles.
    const localRoles = readLocalAdminRoles();
    const derived = resolveBundlePrivileges(localRoles);
    setRoles(localRoles);
    setPrivileges(derived);
    setSource(localRoles.length > 0 ? 'fallback' : 'none');
    setIsLoading(false);
  };

  useEffect(() => {
    load();
    // Re-load when admin login/logout flips the localStorage flag.
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'isAdminAuthenticated' || e.key === 'adminUser') load();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<PrivilegeContextValue>(() => {
    const privSet = new Set(privileges);
    const roleSet = new Set(roles);
    return {
      roles,
      privileges,
      isLoading,
      source,
      has: (key) => privSet.has(key),
      hasAny: (keys) => keys.some((k) => privSet.has(k)),
      hasAll: (keys) => keys.every((k) => privSet.has(k)),
      hasRole: (role) => roleSet.has(role),
      refresh: load,
    };
  }, [roles, privileges, isLoading, source]);

  return <PrivilegeContext.Provider value={value}>{children}</PrivilegeContext.Provider>;
};

export const usePrivileges = (): PrivilegeContextValue => {
  const ctx = useContext(PrivilegeContext);
  if (!ctx) {
    throw new Error('usePrivileges must be used within a PrivilegeProvider');
  }
  return ctx;
};
